import { supabase } from "@/integrations/supabase/client";
import { shouldReport } from "@/lib/errorReportPolicy";

export type ErrorType = "frontend" | "edge_function" | "database" | "auth" | "network";
export type ErrorSeverity = "info" | "warning" | "error" | "critical";

export interface ActionTraceEntry {
  timestamp: string;
  action: string;
  target: string;
  module?: string;
  context?: Record<string, unknown>;
}

export interface ErrorTrackingContext {
  module?: string | null;
  userAction?: string | null;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  actionTrace?: ActionTraceEntry[];
}

interface ErrorLogPayload {
  error_type: ErrorType;
  severity?: ErrorSeverity;
  error_code?: string;
  message: string;
  stack_trace?: string;
  source: string;
  url?: string;
  user_agent?: string;
  user_id?: string;
  company_id?: string;
  details?: Record<string, unknown>;
  request_id?: string;
  // Enriched tracking fields
  module?: string | null;
  user_action?: string | null;
  entity_type?: string;
  entity_id?: string;
  entity_name?: string;
  user_action_trace?: ActionTraceEntry[];
}

// Generate unique request ID for correlation
export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// Main error logging function
export async function logError(
  error: Error | string,
  source: string,
  options: {
    error_type?: ErrorType;
    severity?: ErrorSeverity;
    error_code?: string;
    user_id?: string;
    company_id?: string;
    details?: Record<string, unknown>;
    request_id?: string;
    // Enriched tracking context
    trackingContext?: ErrorTrackingContext;
  } = {}
): Promise<void> {
  try {
    const isError = error instanceof Error;
    const message = isError ? error.message : error;
    const stack_trace = isError ? error.stack : undefined;

    // Puerta única. Cubre las cuatro vías de entrada (window.onerror,
    // unhandledrejection, ErrorBoundary y las llamadas explícitas), así que
    // basta con filtrar aquí. Fuera de producción no se envía nada: los
    // errores de HMR mientras se edita código no son fallos de nadie y eran
    // el 98,6% de las filas de `error_logs`.
    const decision = shouldReport(message, stack_trace);
    if (!decision.report) {
      console.warn(
        `[ErrorLogger] Descartado (${decision.reason} / ${decision.environment}):`,
        source,
        "→",
        message
      );
      return;
    }

    // Diagnostic: always visible in browser console
    console.warn("[ErrorLogger] Sending:", source, "→", message);

    const payload: ErrorLogPayload = {
      error_type: options.error_type || "frontend",
      severity: options.severity || "error",
      error_code: options.error_code,
      message,
      stack_trace,
      source,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      user_id: options.user_id,
      company_id: options.company_id,
      details: options.details,
      request_id: options.request_id || generateRequestId(),
      // Enriched tracking fields from context
      module: options.trackingContext?.module,
      user_action: options.trackingContext?.userAction,
      entity_type: options.trackingContext?.entityType,
      entity_id: options.trackingContext?.entityId,
      entity_name: options.trackingContext?.entityName,
      user_action_trace: options.trackingContext?.actionTrace,
    };

    // Use edge function to log error
    const { error: invokeError } = await supabase.functions.invoke("log-error", {
      body: payload,
    });

    if (invokeError) {
      // Fallback to console if edge function fails
      console.error("[ErrorLogger] Failed to send to server:", invokeError);
      console.error("[ErrorLogger] Original error:", { source, message, stack_trace });
    }
  } catch (logErr) {
    // Never let logging errors crash the app
    console.error("[ErrorLogger] Logging failed:", logErr);
    console.error("[ErrorLogger] Original error:", error);
  }
}

// Global error handler for uncaught errors
export function setupGlobalErrorHandlers(
  getUserContext?: () => { user_id?: string; company_id?: string },
  getTrackingContext?: () => ErrorTrackingContext
): void {
  if (typeof window === "undefined") return;

  // Handle uncaught JS errors
  window.onerror = (message, source, lineno, colno, error) => {
    // El filtrado (ResizeObserver, chunks caducados, extensiones, entorno)
    // vive en logError() → errorReportPolicy, que cubre también las otras vías.
    const userContext = getUserContext?.() || {};
    const trackingContext = getTrackingContext?.();
    
    logError(error || String(message), "window.onerror", {
      error_type: "frontend",
      severity: "error",
      user_id: userContext.user_id,
      company_id: userContext.company_id,
      details: {
        source,
        lineno,
        colno,
      },
      trackingContext,
    });

    // Return false to allow default error handling
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event) => {
    const userContext = getUserContext?.() || {};
    const trackingContext = getTrackingContext?.();
    const error = event.reason;
    
    logError(
      error instanceof Error ? error : String(error),
      "unhandledrejection",
      {
        error_type: "frontend",
        severity: "error",
        user_id: userContext.user_id,
        company_id: userContext.company_id,
        details: {
          type: "unhandled_promise_rejection",
        },
        trackingContext,
      }
    );
  };
}

// Helper to log network/API errors
export async function logNetworkError(
  error: Error | string,
  endpoint: string,
  options: {
    method?: string;
    status?: number;
    user_id?: string;
    company_id?: string;
    request_body?: unknown;
    response_body?: unknown;
    trackingContext?: ErrorTrackingContext;
  } = {}
): Promise<void> {
  await logError(error, `API: ${endpoint}`, {
    error_type: "network",
    severity: options.status && options.status >= 500 ? "critical" : "error",
    user_id: options.user_id,
    company_id: options.company_id,
    details: {
      endpoint,
      method: options.method,
      status: options.status,
      request_body: options.request_body,
      response_body: options.response_body,
    },
    trackingContext: options.trackingContext,
  });
}

// Helper to log auth errors
export async function logAuthError(
  error: Error | string,
  action: string,
  options: {
    user_id?: string;
    email?: string;
    trackingContext?: ErrorTrackingContext;
  } = {}
): Promise<void> {
  await logError(error, `Auth: ${action}`, {
    error_type: "auth",
    severity: "warning",
    user_id: options.user_id,
    details: {
      action,
      email: options.email,
    },
    trackingContext: options.trackingContext,
  });
}
